<template>
  <div class="paros">
    <!-- ===================== BARRA DE CONTROL ===================== -->
    <div class="paros-control">
      <div class="pc-field pc-field--area">
        <span class="pc-label">Nave / Área</span>
        <v-autocomplete v-model="filtros.areas" :items="areas" item-text="nombre" item-value="idArea"
          placeholder="Toda la planta" multiple small-chips deletable-chips clearable :loading="loadingAreas"
          :disabled="loadingReporte" hide-details outlined dense class="pc-ac" />
      </div>

      <div class="pc-field">
        <span class="pc-label">Desde</span>
        <datePicker v-model="filtros.inicioDesde" format="YYYY-MM-DD" :disabled="loadingReporte" maxToday />
      </div>

      <div class="pc-field">
        <span class="pc-label">Hasta</span>
        <datePicker v-model="filtros.inicioHasta" format="YYYY-MM-DD" :disabled="loadingReporte" maxToday />
      </div>

      <div class="pc-field">
        <span class="pc-label">Período</span>
        <div class="pc-seg">
          <button type="button" class="pc-seg-btn" :class="{ 'is-on': periodoActivo === 'hoy' }"
            :disabled="loadingReporte" @click="setPeriodo('hoy')">Hoy</button>
          <button type="button" class="pc-seg-btn" :class="{ 'is-on': periodoActivo === 'semana' }"
            :disabled="loadingReporte" @click="setPeriodo('semana')">Semana</button>
          <button type="button" class="pc-seg-btn" :class="{ 'is-on': periodoActivo === 'mes' }"
            :disabled="loadingReporte" @click="setPeriodo('mes')">Mes</button>
        </div>
      </div>

      <div class="pc-field">
        <span class="pc-label">Tipo de paro</span>
        <v-select v-model="programadoFiltro" :items="tipoParoOpciones" item-text="text" item-value="value"
          :disabled="loadingReporte" hide-details outlined dense class="pc-ac" />
      </div>

      <div class="pc-field">
        <span class="pc-label">Vista</span>
        <v-select v-model="vistaModo" :items="vistaOpciones" item-text="text" item-value="value"
          :disabled="loadingReporte" hide-details outlined dense class="pc-ac" />
      </div>

      <div class="pc-actions">
        <v-progress-circular v-if="loadingReporte" indeterminate :size="20" :width="2" color="#2f6db0"
          class="pc-loading" />

        <!-- El Excel sale con el mismo tipo de paro que está viendo en pantalla: lo define el
             filtro "Tipo de paro", no un menú aparte. -->
        <button type="button" class="pc-btn pc-btn--excel" :disabled="!filtrosValidos || loadingReporte"
          @click="descargarExcel">
          <v-progress-circular v-if="descargando" indeterminate :size="15" :width="2" color="#0a7a4b" />
          <v-icon v-else small color="#0a7a4b">mdi-microsoft-excel</v-icon>
          Exportar
        </button>
      </div>
    </div>

    <LoaderData :loading="loadingReporte" :response="resumenPorMolde" :error="errorReporte">
      <template v-if="chartSeries[0].data.length">
        <!-- ===================== HERO: panel instrumento ===================== -->
        <div class="paros-hero">
          <!-- KPIs en tres tiles del mismo peso; el primero lleva la identidad y el contexto. -->
          <div class="ph-kpis">
            <div class="ph-kpi ph-kpi--brand">
              <div v-if="empresaDisplay" class="ph-brand">{{ empresaDisplay }}</div>
              <div class="ph-context">{{ tipoParoTexto }} · {{ ambitoTexto }} · {{ periodoLabel }}</div>
            </div>
            <div class="ph-kpi">
              <div class="ph-kpi-k">Paro acumulado</div>
              <div class="ph-kpi-v ph-kpi-v--amber">{{ totalHorasTxt }} <small>h</small></div>
            </div>
            <div class="ph-kpi">
              <div class="ph-kpi-k">Paros registrados</div>
              <div class="ph-kpi-v ph-kpi-v--red">{{ formatNumber(totalEventos) }}</div>
            </div>
          </div>

          <!-- Top 5 de mayor paro como mini-cards dentro del panel. -->
          <div class="ph-top" v-if="top5Cards.length">
            <div class="ph-top-head">Top 5 · {{ vistaPluralLower }} con más paro</div>
            <div class="ph-top-cards">
              <div class="ph-top-card" v-for="c in top5Cards" :key="c.label">
                <span class="ph-top-rank">{{ c.rank }}</span>
                <v-icon small class="ph-top-ic">{{ vistaIcon }}</v-icon>
                <span class="ph-top-info">
                  <span class="ph-top-label">{{ c.label }}</span>
                  <span class="ph-top-val">{{ c.valorTxt }} <small>{{ c.unidad }}</small></span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ===================== DISTRIBUCIÓN DEL PARO: barras + pastel ===================== -->
        <section class="paros-card">
          <header class="pcard-head">
            <div>
              <div class="pcard-kicker">Distribución</div>
              <h3 class="pcard-title">Distribución del paro</h3>
            </div>
          </header>
          <div class="pcard-body pcard-split">
            <div class="pcard-pane">
              <apexchart type="bar" height="340" :options="paretoOptions" :series="paretoSeries" />
            </div>
            <div class="pcard-pane pcard-pane--pie">
              <apexchart type="pie" height="340" :options="pieOptions" :series="pieSeries" />
            </div>
          </div>
        </section>

        <!-- ===================== RANKING DE LA VISTA ACTIVA ===================== -->
        <section class="paros-card">
          <header class="pcard-head">
            <div>
              <div class="pcard-kicker">Ranking</div>
              <h3 class="pcard-title">
                {{ (detailViewMode === "cantidad" ? "Paros por " : "Horas de paro por ") + vistaSingularLower }}
              </h3>
            </div>
            <div class="pc-seg pc-seg--sm">
              <button type="button" class="pc-seg-btn" :class="{ 'is-on': detailViewMode === 'tiempo' }"
                @click="detailViewMode = 'tiempo'">Tiempo</button>
              <button type="button" class="pc-seg-btn" :class="{ 'is-on': detailViewMode === 'cantidad' }"
                @click="detailViewMode = 'cantidad'">Eventos</button>
            </div>
          </header>
          <div class="pcard-body">
            <apexchart type="bar" :height="chartHeight" :options="chartOptions" :series="chartSeries" />
          </div>
        </section>
      </template>

      <div v-else class="paros-empty paros-empty--big">
        {{ consultado ? "Sin paros en el período seleccionado." : "Elige un período para ver los paros." }}
      </div>
    </LoaderData>

    <!-- ===================== DIÁLOGO DE DETALLE ===================== -->
    <v-dialog v-model="dialogMolde" max-width="980">
      <v-card class="paros-dialog">
        <div class="pd-head">
          <div class="pd-kicker">Detalle por {{ detalleDimension }}</div>
          <h3 class="pd-title">{{ vistaSingular }} {{ selectedMolde ? selectedMolde.label : "" }}</h3>
        </div>
        <v-card-text class="pt-4">
          <div v-if="detailCausas.length" class="pd-causas">
            <div class="pd-causa" v-for="c in detailCausas" :key="c.name"
              :class="c.esProgramado ? 'is-prog' : 'is-noprog'">
              <span class="pd-causa-dot"></span>
              <span class="pd-causa-name">{{ c.name }}</span>
              <span class="pd-causa-tag">{{ c.esProgramado ? "Programado" : "No programado" }}</span>
              <span class="pd-causa-val">{{ c.valorTxt }} {{ c.unidad }}</span>
            </div>
          </div>
          <div v-if="detailChartSeries.length" class="pcard-body">
            <apexchart type="bar" :height="detailChartHeight" :options="detailChartOptions"
              :series="detailChartSeries" />
          </div>
          <div v-else class="paros-empty">Sin detalle de paros para esta selección.</div>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <button type="button" class="pc-btn pc-btn--ghost" @click="dialogMolde = false">Cerrar</button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import LoaderData from "../../LoaderData.vue";

export default {
  components: {
    LoaderData,
  },
  props: {
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      moldes: [],
      areas: [],
      resumenPorMolde: [],
      resumenPorParo: [],

      loadingMoldes: false,
      loadingAreas: false,
      loadingReporte: false,
      errorReporte: false,
      consultado: false,

      filtros: {
        moldes: [],
        areas: [],
        inicioDesde: null,
        inicioHasta: null,
      },

      detailViewMode: "tiempo",
      vistaModo: "molde",
      dialogMolde: false,
      selectedMolde: null,

      descargando: false,
      periodoActivo: null,
      // Filtro tipo de paro para la vista en pantalla: "todos" | "programados" | "noprogramados".
      programadoFiltro: "todos",
      // Debounce de la consulta reactiva y bandera para no borrar el período al fijar fechas.
      consultaTimer: null,
      fijandoPeriodo: false,

      // Hora de arranque del "día" de la empresa (jornada), de /Horarios/Reportes. Los botones
      // Hoy/Semana/Mes calculan el rango de esa hora a esa hora. Default 06:00 hasta que cargue.
      horaJornada: { hh: 6, mm: 0 },
      jornadaTxt: "06:00",
    };
  },
  computed: {
    ...mapState(["nombreEmpresa"]),
    filtrosValidos() {
      if (!this.filtros.inicioDesde || !this.filtros.inicioHasta) return false;
      return this.filtros.inicioDesde <= this.filtros.inicioHasta;
    },
    // ===== Vista activa: por molde, máquina o tipo de paro (reagrupa el mismo dataset) =====
    vistaOpciones() {
      return [
        { text: "Molde", value: "molde" },
        { text: "Máquina", value: "maquina" },
        { text: "Paros", value: "paro" },
      ];
    },
    vistaSingular() {
      if (this.vistaModo === "maquina") return "Máquina";
      if (this.vistaModo === "paro") return "Tipo de paro";
      return "Molde";
    },
    vistaSingularLower() {
      if (this.vistaModo === "maquina") return "máquina";
      if (this.vistaModo === "paro") return "tipo de paro";
      return "molde";
    },
    vistaPluralLower() {
      if (this.vistaModo === "maquina") return "máquinas";
      if (this.vistaModo === "paro") return "tipos de paro";
      return "moldes";
    },
    detalleDimension() {
      return this.vistaModo === "molde" ? "máquina" : "molde";
    },
    // Icono para las mini-cards del Top 5, según la vista activa.
    vistaIcon() {
      if (this.vistaModo === "maquina") return "mdi-robot-industrial";
      if (this.vistaModo === "paro") return "mdi-alert-circle-outline";
      return "mdi-cube-outline";
    },
    // Nombre a mostrar de la empresa. Caso especial: la instancia "CAZEL-TLT" se muestra
    // como "Tultitlán" (aplica en el hero y en el título del Excel).
    empresaDisplay() {
      return this.nombreEmpresa === "CAZEL-TLT" ? "Tultitlán" : this.nombreEmpresa;
    },
    resumenPorMaquina() {
      const byMaq = new Map();
      this.resumenPorMolde.forEach((molde) => {
        (molde.subRows || []).forEach((maq) => {
          const key = maq.label || "-";
          if (!byMaq.has(key)) {
            byMaq.set(key, {
              label: key,
              codigoMolde: key,
              totalMinutosParo: 0,
              totalEventosAgrupados: 0,
              subRows: [],
            });
          }
          const entry = byMaq.get(key);
          const paros = Array.isArray(maq.parosPorTipoSubtipo) ? maq.parosPorTipoSubtipo : [];
          entry.totalMinutosParo += paros.reduce((a, p) => a + this.toNumber(p.totalMinutosParo), 0);
          entry.totalEventosAgrupados += paros.reduce((a, p) => a + this.getEventosParoValue(p), 0);
          // Sub-fila del detalle en vista máquina: el molde bajo esta máquina.
          entry.subRows.push({ label: molde.label, parosPorTipoSubtipo: paros });
        });
      });
      return Array.from(byMaq.values());
    },
    resumenActivo() {
      if (this.vistaModo === "maquina") return this.resumenPorMaquina;
      if (this.vistaModo === "paro") return this.resumenPorParo;
      return this.resumenPorMolde;
    },
    chartHeight() {
      const total = this.chartSeries[0].data.length;
      return Math.max(340, total * 42 + 80);
    },
    chartSeries() {
      return [
        {
          name: this.detailViewMode === "cantidad" ? "Eventos" : "Horas paro",
          data: this.sortedResumenPorMolde.map((item) =>
            this.detailViewMode === "cantidad"
              ? this.toNumber(item.totalEventosAgrupados)
              : this.minToHoras(item.totalMinutosParo)
          ),
        },
      ];
    },
    // ===== Totales del período (para la sección de resumen en horas) =====
    totalMinutos() {
      return this.resumenPorMolde.reduce((acc, i) => acc + this.toNumber(i.totalMinutosParo), 0);
    },
    totalEventos() {
      return this.resumenPorMolde.reduce((acc, i) => acc + this.toNumber(i.totalEventosAgrupados), 0);
    },
    totalHorasTxt() {
      return (this.totalMinutos / 60).toLocaleString("en-US", { maximumFractionDigits: 1 });
    },
    ambitoTexto() {
      if (this.filtros.areas.length) {
        const nombres = this.areas
          .filter((a) => this.filtros.areas.includes(a.idArea))
          .map((a) => a.nombre);
        return nombres.length ? nombres.join(", ") : "Naves seleccionadas";
      }
      return "Toda la planta";
    },
    // Opciones del desplegable "Tipo de paro" (mismo control que el de Nave / Área).
    tipoParoOpciones() {
      return [
        { text: "Todos", value: "todos" },
        { text: "Programados", value: "programados" },
        { text: "No programados", value: "noprogramados" },
      ];
    },
    // Etiqueta del hero según el filtro de tipo de paro (programado / no programado / todos).
    tipoParoTexto() {
      if (this.programadoFiltro === "programados") return "Paro programado acumulado";
      if (this.programadoFiltro === "noprogramados") return "Paro no programado acumulado";
      return "Paro acumulado";
    },
    // Molde con mayor impacto (más horas de paro) para el hero.
    topOffender() {
      const first = this.sortedResumenPorMolde[0];
      if (!first) return null;
      const horas = this.minToHoras(first.totalMinutosParo);
      return {
        codigo: first.label,
        horasTxt: horas.toLocaleString("en-US", { maximumFractionDigits: 1 }),
      };
    },
    periodoLabel() {
      const d = this.filtros.inicioDesde;
      const h = this.filtros.inicioHasta;
      if (this.periodoActivo === "hoy") return `Hoy · ${this.fechaLarga(d)}`;
      if (this.periodoActivo === "semana") return `Semana · del ${this.fechaCorta(d)} al ${this.fechaCorta(h)}`;
      if (this.periodoActivo === "mes") {
        return `Mes · ${this.nombreMes(d)} (del ${this.fechaCorta(d)} al ${this.fechaCorta(h)})`;
      }
      if (d && h) return `Del ${this.fechaCorta(d)} al ${this.fechaCorta(h)}`;
      return "Sin período";
    },
    // Cuántos moldes concentran el 80% del tiempo de paro (regla 80/20).
    moldesPara80() {
      const vals = this.sortedResumenPorMolde.map((i) => this.toNumber(i.totalMinutosParo));
      const total = vals.reduce((a, b) => a + b, 0);
      if (total <= 0) return 0;
      let acc = 0;
      let n = 0;
      for (const v of vals) {
        acc += v;
        n += 1;
        if (acc / total >= 0.8) break;
      }
      return n;
    },
    // Ancho de la barra del hero: cuánto concentra el molde de mayor impacto.
    barraTopWidth() {
      const total = this.totalMinutos;
      const first = this.sortedResumenPorMolde[0];
      if (!total || !first) return "0%";
      const pct = (this.toNumber(first.totalMinutosParo) / total) * 100;
      return `${Math.min(100, Math.max(6, pct))}%`;
    },
    // Los 5 moldes/máquinas de mayor impacto (mini-cards del hero y rebanadas del pastel).
    top5Resumen() {
      return this.sortedResumenPorMolde.slice(0, 5);
    },
    // Mini-cards del hero: top 5 con su valor en la métrica activa (horas o paros).
    top5Cards() {
      return this.top5Resumen.map((item, i) => {
        const val =
          this.detailViewMode === "cantidad"
            ? this.toNumber(item.totalEventosAgrupados)
            : this.minToHoras(item.totalMinutosParo);
        return {
          rank: i + 1,
          label: item.label,
          valorTxt: this.formatMetric(val),
          unidad: this.detailViewMode === "cantidad" ? "paros" : "h",
        };
      });
    },
    // ===== Distribución: barras de TODOS los moldes/máquinas (horas o eventos, desc) =====
    paretoCategorias() {
      return this.sortedResumenPorMolde.map((i) => i.label);
    },
    paretoSeries() {
      const valores = this.sortedResumenPorMolde.map((i) =>
        this.detailViewMode === "cantidad"
          ? this.toNumber(i.totalEventosAgrupados)
          : this.minToHoras(i.totalMinutosParo)
      );
      return [
        {
          name: this.detailViewMode === "cantidad" ? "Paros" : "Horas",
          data: valores.map((v) => Math.round(v * 10) / 10),
        },
      ];
    },
    paretoOptions() {
      return {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
        dataLabels: { enabled: false },
        xaxis: { categories: this.paretoCategorias, title: { text: this.vistaSingular } },
        yaxis: {
          title: { text: this.detailViewMode === "cantidad" ? "Paros" : "Horas" },
          labels: { formatter: (v) => this.formatMetric(v) },
        },
        colors: ["#e8833a"],
        tooltip: {
          y: {
            formatter: (v) =>
              this.detailViewMode === "cantidad"
                ? `${this.formatMetric(v)} paros`
                : `${this.formatMetric(v)} h`,
          },
        },
        grid: { borderColor: "#e4ebf5" },
      };
    },
    // ===== Pastel: los 5 de mayor impacto como rebanadas + "Otros" agrupado =====
    piePartes() {
      const items = this.sortedResumenPorMolde.map((i) => ({
        label: i.label,
        val:
          this.detailViewMode === "cantidad"
            ? this.toNumber(i.totalEventosAgrupados)
            : this.minToHoras(i.totalMinutosParo),
      }));
      const TOP = 5;
      const top = items.slice(0, TOP);
      const otros = items.slice(TOP).reduce((a, b) => a + b.val, 0);
      const labels = top.map((t) => t.label);
      const series = top.map((t) => Math.round(t.val * 10) / 10);
      if (otros > 0) {
        labels.push("Otros");
        series.push(Math.round(otros * 10) / 10);
      }
      return { labels, series };
    },
    pieSeries() {
      return this.piePartes.series;
    },
    pieOptions() {
      return {
        chart: { type: "pie", toolbar: { show: false } },
        labels: this.piePartes.labels,
        legend: { position: "bottom", fontFamily: "inherit" },
        dataLabels: { formatter: (val) => `${Math.round(val)}%` },
        stroke: { width: 2, colors: ["#fff"] },
        colors: ["#e8833a", "#f5a524", "#2f6db0", "#5ac8fa", "#8b5cf6", "#e5484d", "#9aa7bd"],
        tooltip: {
          y: {
            formatter: (v) =>
              this.detailViewMode === "cantidad"
                ? `${this.formatMetric(v)} paros`
                : `${this.formatMetric(v)} h`,
          },
        },
        responsive: [{ breakpoint: 600, options: { legend: { position: "bottom" } } }],
      };
    },
    // Base de todas las gráficas y del Top 5. El backend devuelve también moldes/máquinas sin paro
    // en el rango (o sin paro del tipo filtrado), así que se descartan los que no aportan valor en
    // la métrica activa: si no tienen dato, no ocupan lugar en el eje ni estiran la tarjeta.
    sortedResumenPorMolde() {
      const sorted = this.resumenActivo.filter((item) => this.valorMetrica(item) > 0);
      sorted.sort((a, b) => this.valorMetrica(b) - this.valorMetrica(a));
      return sorted;
    },
    // Sub-filas del detalle (máquinas en vista molde; moldes en vista máquina), ordenadas
    // por impacto en la métrica activa.
    selectedMoldeMachines() {
      if (!this.selectedMolde) return [];
      const subRows = Array.isArray(this.selectedMolde.subRows) ? this.selectedMolde.subRows : [];
      const valor = (row) => {
        const paros = Array.isArray(row.parosPorTipoSubtipo) ? row.parosPorTipoSubtipo : [];
        return paros.reduce(
          (acc, paro) =>
            acc +
            (this.detailViewMode === "cantidad"
              ? this.getEventosParoValue(paro)
              : this.toNumber(paro.totalMinutosParo)),
          0
        );
      };
      // Mismo criterio que el ranking: las sub-filas sin valor en la métrica activa no se grafican.
      return subRows.filter((r) => valor(r) > 0).sort((a, b) => valor(b) - valor(a));
    },
    detailMachineLabels() {
      return this.selectedMoldeMachines.map((r) => r.label || "-");
    },
    // Causas del detalle con su bandera programado / no programado (del catálogo ParoTipo),
    // ordenadas por impacto en la métrica activa. Se muestran como chips en el diálogo.
    detailCausas() {
      const map = new Map();
      this.selectedMoldeMachines.forEach((fila) => {
        (Array.isArray(fila.parosPorTipoSubtipo) ? fila.parosPorTipoSubtipo : []).forEach((paro) => {
          const name =
            paro.nombreSubParoTipo ||
            paro.nombreParoTipo ||
            `Causa ${paro.idSubParoTipo || paro.idParoTipo || "-"}`;
          if (!map.has(name)) {
            map.set(name, { name, esProgramado: !!paro.esProgramado, minutos: 0, eventos: 0 });
          }
          const e = map.get(name);
          e.minutos += this.toNumber(paro.totalMinutosParo);
          e.eventos += this.getEventosParoValue(paro);
        });
      });
      const arr = Array.from(map.values());
      arr.sort((a, b) =>
        this.detailViewMode === "cantidad" ? b.eventos - a.eventos : b.minutos - a.minutos
      );
      return arr.map((c) => ({
        name: c.name,
        esProgramado: c.esProgramado,
        valorTxt: this.formatMetric(
          this.detailViewMode === "cantidad" ? c.eventos : this.minToHoras(c.minutos)
        ),
        unidad: this.detailViewMode === "cantidad" ? "paros" : "h",
      }));
    },
    detailChartSeries() {
      const filas = this.selectedMoldeMachines;
      if (!filas.length) return [];

      const causes = [];
      const causeSet = new Set();
      filas.forEach((fila) => {
        (Array.isArray(fila.parosPorTipoSubtipo) ? fila.parosPorTipoSubtipo : []).forEach((paro) => {
          const cause =
            paro.nombreSubParoTipo ||
            paro.nombreParoTipo ||
            `Causa ${paro.idSubParoTipo || paro.idParoTipo || "-"}`;
          if (!causeSet.has(cause)) {
            causeSet.add(cause);
            causes.push(cause);
          }
        });
      });

      return causes.map((cause) => ({
        name: cause,
        data: filas.map((fila) => {
          const paros = Array.isArray(fila.parosPorTipoSubtipo) ? fila.parosPorTipoSubtipo : [];
          return paros.reduce((acc, paro) => {
            const currentCause =
              paro.nombreSubParoTipo ||
              paro.nombreParoTipo ||
              `Causa ${paro.idSubParoTipo || paro.idParoTipo || "-"}`;
            if (currentCause !== cause) return acc;
            // En "tiempo" el valor va en HORAS (antes usaba minutos y por eso se veían valores
            // muy elevados en el detalle).
            const value =
              this.detailViewMode === "cantidad"
                ? this.getEventosParoValue(paro)
                : this.minToHoras(paro.totalMinutosParo);
            return acc + value;
          }, 0);
        }),
      }));
    },
    detailChartHeight() {
      const total = this.detailMachineLabels.length;
      return Math.max(320, total * 56 + 70);
    },
    chartOptions() {
      return {
        chart: {
          toolbar: { show: false },
          events: {
            dataPointSelection: (event, chartContext, config) => {
              this.onMoldeBarClick(config.dataPointIndex);
            },
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 5,
            distributed: false,
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (value) => this.formatMetric(value),
        },
        xaxis: {
          min: 0,
          categories: this.sortedResumenPorMolde.map((item) => item.label),
          title: {
            text: this.detailViewMode === "cantidad" ? "Cantidad de paros" : "Horas de paro",
          },
          labels: {
            formatter: (value) => this.formatMetric(value),
          },
        },
        yaxis: {
          title: {
            text: this.vistaSingular,
          },
        },
        tooltip: {
          y: {
            formatter: (value) => {
              if (this.detailViewMode === "cantidad") {
                return `${this.formatMetric(value)} eventos`;
              }
              return `${this.formatMetric(value)} h`;
            },
          },
        },
        colors: ["#e8833a"],
        grid: {
          borderColor: "#eef2f8",
        },
      };
    },
    detailChartOptions() {
      return {
        chart: {
          stacked: true,
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
          },
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          min: 0,
          categories: this.detailMachineLabels,
          title: {
            text: this.detailViewMode === "cantidad" ? "Cantidad de paros" : "Horas de paro",
          },
          labels: {
            formatter: (value) => this.formatMetric(value),
          },
        },
        yaxis: {
          title: {
            text: "",
          },
        },
        legend: {
          position: "bottom",
          showForSingleSeries: true,
        },
        tooltip: {
          y: {
            formatter: (value) => {
              if (this.detailViewMode === "cantidad") {
                return `${this.formatMetric(value)} eventos`;
              }
              return `${this.formatMetric(value)} h`;
            },
          },
        },
        grid: {
          borderColor: "#e4ebf5",
        },
      };
    },
  },
  watch: {
    active(newVal) {
      if (newVal && !this.consultado && this.filtrosValidos) {
        this.consultar();
      }
    },
    // Sin botón "Consultar": la consulta se dispara (con debounce) al mover cualquier filtro.
    "filtros.inicioDesde"() {
      if (!this.fijandoPeriodo) this.periodoActivo = null;
      this.scheduleConsulta();
    },
    "filtros.inicioHasta"() {
      if (!this.fijandoPeriodo) this.periodoActivo = null;
      this.scheduleConsulta();
    },
    "filtros.areas": {
      handler() {
        this.scheduleConsulta();
      },
      deep: true,
    },
    programadoFiltro() {
      this.scheduleConsulta();
    },
    vistaModo() {
      this.dialogMolde = false;
      this.selectedMolde = null;
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.getMoldes();
      this.getAreas();
      // Carga la hora de jornada y, al terminar, fija el período por defecto (semana) ya con la hora
      // correcta. La consulta real se dispara cuando la pestaña está activa.
      this.getHorarioJornada();
    },
    // Lee /Horarios/Reportes (endpoint sin permiso) y toma la hora de inicio del Horario con menor
    // día de la semana = arranque del "día" de la empresa. Mismo criterio que el backend.
    getHorarioJornada() {
      axios
        .get("/Horarios/Reportes")
        .then((response) => {
          const d = response.data || {};
          const eventos = Array.isArray(d.events) ? d.events : Array.isArray(d) ? d : [];
          if (eventos.length) {
            const menor = eventos.reduce((min, h) =>
              Number(h.diaSemana) < Number(min.diaSemana) ? h : min
            );
            const [hh, mm] = String(menor.inicio || "06:00").split(":").map((n) => parseInt(n, 10));
            if (Number.isFinite(hh)) {
              this.horaJornada = { hh, mm: Number.isFinite(mm) ? mm : 0 };
              const p2 = (n) => `${n}`.padStart(2, "0");
              this.jornadaTxt = `${p2(this.horaJornada.hh)}:${p2(this.horaJornada.mm)}`;
            }
          }
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.setPeriodo("semana");
        });
    },
    // Convierte "YYYY-MM-DD" al datetime de jornada "YYYY-MM-DDTHH:mm:00" (+addDays días), para
    // mandar al backend el rango con la hora de arranque de la empresa.
    toJornadaDT(dateStr, addDays) {
      if (!dateStr) return dateStr;
      const p = String(dateStr).split("-").map((n) => parseInt(n, 10));
      if (p.length < 3) return dateStr;
      const dt = new Date(p[0], p[1] - 1, p[2] + (addDays || 0), this.horaJornada.hh, this.horaJornada.mm, 0);
      const p2 = (n) => `${n}`.padStart(2, "0");
      return `${dt.getFullYear()}-${p2(dt.getMonth() + 1)}-${p2(dt.getDate())}T${p2(dt.getHours())}:${p2(dt.getMinutes())}:00`;
    },
    setDefaultDates() {
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(today.getDate() - 7);

      this.filtros.inicioHasta = this.toDateInput(today);
      this.filtros.inicioDesde = this.toDateInput(sevenDaysAgo);
    },
    toDateInput(date) {
      const d = new Date(date);
      const month = `${d.getMonth() + 1}`.padStart(2, "0");
      const day = `${d.getDate()}`.padStart(2, "0");
      return `${d.getFullYear()}-${month}-${day}`;
    },
    toNumber(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    },
    getEventosParoValue(paro) {
      return this.toNumber(paro && (paro.totalEventosAgrupados ?? paro.totalEventos));
    },
    // Valor de una fila (molde o máquina) en la métrica activa: eventos o minutos de paro.
    valorMetrica(item) {
      return this.detailViewMode === "cantidad"
        ? this.toNumber(item.totalEventosAgrupados)
        : this.toNumber(item.totalMinutosParo);
    },
    formatNumber(value) {
      return this.toNumber(value).toLocaleString("en-US");
    },
    minToHoras(min) {
      return this.toNumber(min) / 60;
    },
    // Formato de fechas para la etiqueta de período (las fechas llegan como "YYYY-MM-DD").
    fechaCorta(iso) {
      if (!iso) return "—";
      const p = String(iso).split("-");
      return p.length === 3 ? `${p[2]}/${p[1]}` : iso;
    },
    fechaLarga(iso) {
      if (!iso) return "—";
      const p = String(iso).split("-");
      return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
    },
    nombreMes(iso) {
      if (!iso) return "";
      const meses = [
        "enero", "febrero", "marzo", "abril", "mayo", "junio",
        "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
      ];
      const p = String(iso).split("-");
      if (p.length < 2) return "";
      const nombre = meses[parseInt(p[1], 10) - 1] || "";
      return nombre ? `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} ${p[0]}` : p[0];
    },
    // Formatea el valor ya en su unidad de métrica: enteros para "cantidad", 1 decimal para horas.
    formatMetric(value) {
      if (this.detailViewMode === "cantidad") {
        return Math.round(this.toNumber(value)).toLocaleString("en-US");
      }
      return this.toNumber(value).toLocaleString("en-US", { maximumFractionDigits: 1 });
    },
    // Consulta reactiva con debounce: coalesce los cambios de varios filtros (p. ej. las dos
    // fechas de un período) en una sola llamada al backend.
    scheduleConsulta() {
      // Solo consulta si la pestaña está activa y los filtros son válidos: evita llamadas mientras
      // el reporte está oculto (al abrirlo, el watcher de "active" hace la primera consulta).
      if (!this.active || !this.filtrosValidos) return;
      clearTimeout(this.consultaTimer);
      this.consultaTimer = setTimeout(() => this.consultar(), 300);
    },
    // Botones de período: fija el rango de fechas (la consulta la disparan los watchers de fecha).
    // lunes = inicio de semana. "fijandoPeriodo" evita que el watcher borre el período activo.
    setPeriodo(tipo) {
      const hoy = new Date();
      let desde = new Date(hoy);
      if (tipo === "semana") {
        const dia = (hoy.getDay() + 6) % 7;
        desde = new Date(hoy);
        desde.setDate(hoy.getDate() - dia);
      } else if (tipo === "mes") {
        desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      }
      this.fijandoPeriodo = true;
      this.periodoActivo = tipo;
      this.filtros.inicioDesde = this.toDateInput(desde);
      this.filtros.inicioHasta = this.toDateInput(hoy);
      this.$nextTick(() => {
        this.fijandoPeriodo = false;
      });
      this.scheduleConsulta();
    },
    // Descarga el Excel del backend con los mismos filtros de la vista; el tipo de paro
    // (todos / programados / no programados) lo determina el filtro "Tipo de paro".
    descargarExcel() {
      if (!this.filtrosValidos || this.descargando) return;
      const programado =
        this.programadoFiltro === "programados"
          ? true
          : this.programadoFiltro === "noprogramados"
            ? false
            : null;
      this.descargando = true;
      const params = new URLSearchParams();
      this.filtros.areas.forEach((id) => params.append("idArea", id));
      params.append("inicioDesde", this.toJornadaDT(this.filtros.inicioDesde, 0));
      params.append("inicioHasta", this.toJornadaDT(this.filtros.inicioHasta, 1));
      params.append("metrica", this.detailViewMode === "cantidad" ? "cantidad" : "tiempo");
      if (programado === true || programado === false) params.append("programado", programado);
      if (this.empresaDisplay) params.append("empresa", this.empresaDisplay);
      axios
        .get(`/ReportesMoldes/ParosMoldeMaquinaExcel?${params.toString()}`, { responseType: "blob" })
        .then((response) => {
          const tipoNombre =
            programado === true ? "Programados" : programado === false ? "NoProgramados" : "Todos";
          const nombre = `ParosPorMolde-${tipoNombre}.xlsx`;
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = URL.createObjectURL(blob);
          const enlace = document.createElement("a");
          enlace.href = url;
          enlace.download = nombre;
          document.body.appendChild(enlace);
          enlace.click();
          document.body.removeChild(enlace);
          URL.revokeObjectURL(url);
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.descargando = false;
        });
    },
    getMoldes() {
      this.loadingMoldes = true;
      axios
        .get("/Moldes")
        .then((response) => {
          this.moldes = Array.isArray(response.data) ? response.data : [];
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.loadingMoldes = false;
        });
    },
    getAreas() {
      this.loadingAreas = true;
      axios
        .get("/Areas")
        .then((response) => {
          const data = Array.isArray(response.data) ? response.data : [];
          this.areas = data.filter((a) => a.idArea > 1);
        })
        .catch((error) => console.log(error))
        .finally(() => {
          this.loadingAreas = false;
        });
    },
    buildQuery() {
      const params = new URLSearchParams();
      this.filtros.moldes.forEach((id) => params.append("idMolde", id));
      this.filtros.areas.forEach((id) => params.append("idArea", id));
      // Rango con hora de jornada: desde = día@hora, hasta = (últimoDía+1)@hora (exclusivo).
      params.append("inicioDesde", this.toJornadaDT(this.filtros.inicioDesde, 0));
      params.append("inicioHasta", this.toJornadaDT(this.filtros.inicioHasta, 1));
      if (this.programadoFiltro === "programados") params.append("programado", true);
      else if (this.programadoFiltro === "noprogramados") params.append("programado", false);
      return params.toString();
    },
    mapReporte(payload) {
      if (!Array.isArray(payload)) return [];

      const mapped = payload.map((item) => {
        const moldesMaquinaRaw = Array.isArray(item.moldesMaquina) ? item.moldesMaquina : [];
        const byMachine = new Map();

        moldesMaquinaRaw.forEach((mm) => {
          const machineKey = `${mm.idMaquina || ""}-${mm.nombreMaquina || ""}`;
          const paros = Array.isArray(mm.parosPorTipoSubtipo) ? mm.parosPorTipoSubtipo : [];
          if (!byMachine.has(machineKey)) {
            byMachine.set(machineKey, {
              idMaquina: mm.idMaquina,
              nombreMaquina: mm.nombreMaquina || "-",
              parosPorTipoSubtipo: [],
            });
          }
          const machineRow = byMachine.get(machineKey);
          machineRow.parosPorTipoSubtipo = machineRow.parosPorTipoSubtipo.concat(paros);
        });

        const moldesMaquina = Array.from(byMachine.values());

        const totalMinutosParo = moldesMaquina.reduce((acumMaquina, maquina) => {
          const paros = Array.isArray(maquina.parosPorTipoSubtipo) ? maquina.parosPorTipoSubtipo : [];
          return (
            acumMaquina +
            paros.reduce((acumParo, paro) => {
              return acumParo + this.toNumber(paro.totalMinutosParo);
            }, 0)
          );
        }, 0);
        const totalEventosAgrupados = moldesMaquina.reduce((acumMaquina, maquina) => {
          const paros = Array.isArray(maquina.parosPorTipoSubtipo) ? maquina.parosPorTipoSubtipo : [];
          return (
            acumMaquina +
            paros.reduce((acumParo, paro) => {
              return acumParo + this.getEventosParoValue(paro);
            }, 0)
          );
        }, 0);

        return {
          label: item.codigoMolde || "-",
          codigoMolde: item.codigoMolde || "-",
          totalMinutosParo,
          totalEventosAgrupados,
          // Sub-filas para el detalle: en vista molde son las máquinas.
          subRows: moldesMaquina.map((mm) => ({
            label: mm.nombreMaquina || "-",
            parosPorTipoSubtipo: Array.isArray(mm.parosPorTipoSubtipo) ? mm.parosPorTipoSubtipo : [],
          })),
          raw: {
            ...item,
            moldesMaquina,
          },
        };
      });

      return mapped.sort((a, b) => b.totalMinutosParo - a.totalMinutosParo);
    },
    mapReportePorTipo(payload) {
      if (!Array.isArray(payload)) return [];

      return payload.map((item) => {
        const idTipo = item.idParoTipo == null ? null : String(item.idParoTipo);
        const label = item.nombreParoTipo || (idTipo ? `Tipo de paro ${idTipo}` : "Sin tipo");
        return {
          label,
          codigoMolde: label,
          totalMinutosParo: this.toNumber(item.totalMinutosParo),
          totalEventosAgrupados: this.toNumber(item.totalEventosAgrupados),
          subRows: (Array.isArray(item.moldes) ? item.moldes : []).map((molde) => ({
            label: molde.codigoMolde || "-",
            parosPorTipoSubtipo: Array.isArray(molde.parosPorTipoSubtipo)
              ? molde.parosPorTipoSubtipo
              : [],
          })),
          raw: item,
        };
      });
    },
    onMoldeBarClick(index) {
      if (index < 0 || index >= this.sortedResumenPorMolde.length) return;
      this.selectedMolde = this.sortedResumenPorMolde[index];
      this.dialogMolde = true;
    },
    consultar() {
      if (!this.filtrosValidos) return;

      this.loadingReporte = true;
      this.errorReporte = false;

      axios
        .get(`/ReportesMoldes/ResumenMoldesMultimaquinaParos?${this.buildQuery()}`)
        .then((response) => {
          this.consultado = true;
          this.resumenPorMolde = this.mapReporte(response.data && response.data.porMolde);
          this.resumenPorParo = this.mapReportePorTipo(response.data && response.data.porTipoParo);
        })
        .catch((error) => {
          console.log(error);
          this.errorReporte = true;
          this.resumenPorMolde = [];
          this.resumenPorParo = [];
        })
        .finally(() => {
          this.loadingReporte = false;
        });
    },
  },
};
</script>

<style scoped>
/* ============================================================
   Reporte de Paros por Molde — "Sala de control" industrial.
   Firma: hero oscuro tipo tablero con la lectura del paro
   acumulado (contador ámbar) y la regla 80/20.
   ============================================================ */
.paros {
  --ink: #0c1220;
  --ink-2: #14203a;
  --paper: #f3f6fb;
  --line: #e4eaf3;
  --muted: #66768d;
  --text: #16202f;
  --amber: #f5a524;
  --amber-2: #e8833a;
  --red: #e5484d;
  --steel: #2f6db0;
  color: var(--text);
  font-family: 'Space Grotesk', 'Roboto', 'Segoe UI', sans-serif;
}

/* ---------- Barra de control ---------- */
.paros-control {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px 12px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow: 0 6px 22px rgba(20, 40, 80, 0.05);
}

/* Todos los campos crecen parejo para repartirse el ancho (distribución equilibrada);
   con flex-wrap la barra se reacomoda sola al angostar (responsiva). */
.pc-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 132px;
  min-width: 120px;
}

.pc-field--area {
  flex: 1 1 160px;
  min-width: 150px;
  max-width: 240px;
}

.pc-label {
  color: var(--muted);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.pc-ac {
  margin-top: -2px;
}

/* Inputs de terceros (datepicker / autocomplete): mismo radio y alto para que el
   control se vea de una pieza. */
.pc-field ::v-deep .field-input,
.pc-field ::v-deep .v-input__slot {
  background: #f6f8fc !important;
  border-radius: 12px !important;
  min-height: 44px !important;
}

.pc-field ::v-deep .field-input {
  border: 1px solid var(--line) !important;
  box-shadow: none !important;
  color: var(--text) !important;
}

/* Contenido del autocomplete/inputs bien visible dentro de la casilla. */
.pc-field ::v-deep fieldset {
  border-color: var(--line) !important;
  border-radius: 12px !important;
}

.pc-field ::v-deep input {
  color: var(--text) !important;
}

.pc-field ::v-deep input::placeholder {
  color: #8592a6 !important;
  opacity: 1;
}

.pc-field ::v-deep .v-chip {
  background: #e5edf8 !important;
  color: var(--text) !important;
  font-weight: 600;
}

.pc-field ::v-deep .v-select__selection {
  color: var(--text) !important;
}

/* ---------- Segmented control (período / métrica) ---------- */
.pc-seg {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: #eef2f8;
  border: 1px solid #e0e7f1;
  border-radius: 13px;
  padding: 3px;
}

.pc-seg-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 0;
  background: transparent;
  color: #55647b;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  padding: 8px 11px;
  border-radius: 10px;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

/* El icono del segmented sigue el color del botón (gris → blanco al activarse). */
.pc-seg-btn ::v-deep .v-icon {
  color: inherit !important;
  font-size: 17px !important;
}

.pc-seg-btn:hover:not(.is-on):not(:disabled) {
  color: var(--text);
}

.pc-seg-btn.is-on {
  background: var(--ink);
  color: #fff;
  box-shadow: 0 3px 8px rgba(12, 18, 32, 0.28);
}

.pc-seg-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.pc-seg--sm .pc-seg-btn {
  padding: 7px 13px;
  font-size: 12px;
}

/* En la barra de filtros los segmentados llenan su campo y sus botones se reparten parejo. */
.paros-control .pc-seg {
  display: flex;
  width: 100%;
}

.paros-control .pc-seg .pc-seg-btn {
  flex: 1 1 auto;
  justify-content: center;
}

/* ---------- Botones ---------- */
.pc-actions {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex: 0 0 auto;
  margin-left: auto;
}

/* Spinner de carga (la consulta es reactiva; no hay botón Consultar). */
.pc-loading {
  align-self: center;
  margin-bottom: 12px;
}

.pc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 44px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  border: 1px solid transparent;
  border-radius: 13px;
  padding: 0 18px;
  transition: transform 0.12s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.pc-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.pc-btn:not(:disabled):active {
  transform: translateY(1px);
}

.pc-btn--primary {
  background: var(--steel);
  color: #fff;
  box-shadow: 0 6px 16px rgba(47, 109, 176, 0.32);
}

.pc-btn--primary:hover:not(:disabled) {
  background: #275e99;
}

.pc-btn--excel {
  background: #eafaf1;
  border-color: #b8e6cf;
  color: #0a7a4b;
}

.pc-btn--excel:hover:not(:disabled) {
  background: #d9f5e6;
}

.pc-btn--ghost {
  background: #eef2f8;
  color: var(--text);
  min-height: 40px;
}

.pc-btn--ghost:hover {
  background: #e2e9f4;
}

/* ---------- HERO: panel de indicadores (tema claro, alto contraste) ---------- */
.paros-hero {
  position: relative;
  overflow: hidden;
  margin-top: 16px;
  padding: 20px 22px;
  border: 1px solid var(--line);
  border-top: 3px solid var(--amber-2);
  border-radius: 20px;
  background: linear-gradient(180deg, #fbfcfe, #ffffff);
  box-shadow: 0 6px 22px rgba(20, 40, 80, 0.06);
  animation: paros-rise 0.5s ease both;
}

/* Nombre de la empresa: marca oscura con línea ámbar, dentro del tile de identidad. */
.ph-brand {
  display: inline-block;
  align-self: flex-start;
  color: var(--text);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 6px;
  padding-bottom: 4px;
  border-bottom: 2px solid var(--amber-2);
}

.ph-context {
  color: var(--muted);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.45;
}

/* KPIs: tres tiles del mismo peso, repartidos parejo. */
.ph-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px;
  margin: 16px 0 4px;
}

.ph-kpi {
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: #f6f8fc;
}

/* Tercer tile: identidad (empresa) + contexto, centrado verticalmente. */
.ph-kpi--brand {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.ph-kpi-k {
  color: var(--muted);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.ph-kpi-v {
  font-family: 'JetBrains Mono', 'SFMono-Regular', 'Consolas', monospace;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.01em;
  margin-top: 5px;
}

.ph-kpi-v small {
  font-family: inherit;
  color: var(--muted);
  font-size: 15px;
  font-weight: 700;
}

.ph-kpi-v--amber {
  color: var(--amber-2);
}

.ph-kpi-v--red {
  color: var(--red);
}

.ph-kpi-note {
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
  margin-top: 3px;
}

/* ---------- Top 5 mini-cards dentro del panel ---------- */
.ph-top {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid var(--line);
}

.ph-top-head {
  color: var(--muted);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.ph-top-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(164px, 1fr));
  gap: 10px;
}

.ph-top-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #f6f8fc;
  min-width: 0;
}

.ph-top-rank {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: var(--amber-2);
  color: #fff;
  font-size: 13px;
  font-weight: 800;
}

.ph-top-ic {
  flex: 0 0 auto;
  color: var(--amber-2) !important;
  font-size: 19px !important;
}

.ph-top-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.ph-top-label {
  color: var(--text);
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ph-top-val {
  color: var(--amber-2);
  font-size: 13px;
  font-weight: 700;
}

.ph-top-val small {
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
}

/* ---------- Cards de análisis ---------- */
.paros-card {
  margin-top: 16px;
  overflow: hidden;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 18px;
  box-shadow: 0 6px 22px rgba(20, 40, 80, 0.05);
}

.pcard-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #eef2f8;
}

.pcard-kicker {
  color: var(--amber-2);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.pcard-title {
  color: var(--text);
  font-size: 1.15rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 3px 0 0;
}

.pcard-body {
  padding: 12px 14px;
}

/* Análisis 80/20: Pareto (izquierda) + pastel (derecha), responsivo. */
.pcard-split {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
}

.pcard-pane {
  flex: 1 1 360px;
  min-width: 280px;
}

.pcard-pane--pie {
  flex: 1 1 300px;
  max-width: 440px;
}

.paros-empty {
  color: var(--muted);
  font-size: 0.95rem;
  padding: 14px;
  text-align: center;
}

.paros-empty--big {
  margin-top: 16px;
  padding: 52px 16px;
  font-size: 1.05rem;
  background: #fff;
  border: 1px dashed var(--line);
  border-radius: 18px;
}

/* ---------- Diálogo de detalle ---------- */
.paros-dialog {
  border-radius: 18px !important;
  overflow: hidden;
}

.pd-head {
  padding: 18px 22px;
  background: var(--ink);
}

.pd-kicker {
  color: var(--amber-2);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.pd-title {
  color: #fff;
  font-size: 1.2rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 3px 0 0;
}

/* Detalle: causas con bandera programado (azul) / no programado (rojo). */
.pd-causas {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.pd-causa {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: #f6f8fc;
  font-size: 12.5px;
}

.pd-causa-dot {
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.pd-causa.is-prog .pd-causa-dot {
  background: var(--steel);
}

.pd-causa.is-noprog .pd-causa-dot {
  background: var(--red);
}

.pd-causa-name {
  color: var(--text);
  font-weight: 700;
}

.pd-causa-tag {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.pd-causa.is-prog .pd-causa-tag {
  color: var(--steel);
}

.pd-causa.is-noprog .pd-causa-tag {
  color: var(--red);
}

.pd-causa-val {
  color: var(--muted);
  font-weight: 700;
}

@keyframes paros-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
}

@media (max-width: 720px) {
  .pc-actions {
    margin-left: 0;
    width: 100%;
  }
  .pc-btn {
    flex: 1 1 auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .paros-hero {
    animation: none;
  }
  .ph-bar span {
    transition: none;
  }
}
</style>
